import React, { useState } from 'react';
import { LockIcon, MailIcon, EyeIcon, EyeOffIcon, ArrowRightIcon, ShieldIcon, Building2Icon, UserCheckIcon, UserIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
interface LoginPageProps {
  onLogin: (role: string) => void;
}
export function LoginPage({
  onLogin
}: LoginPageProps) {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState('tenant_admin');
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setTimeout(() => {
      setIsLoading(false);
      onLogin(selectedRole);
    }, 1000);
  };
  const roles = [{
    id: 'super_admin',
    label: 'Super Admin',
    desc: 'Platform Management',
    icon: ShieldIcon
  }, {
    id: 'tenant_admin',
    label: 'Company Admin',
    desc: 'Organization Management',
    icon: Building2Icon
  }, {
    id: 'manager',
    label: 'Manager',
    desc: 'Team & Approvals',
    icon: UserCheckIcon
  }, {
    id: 'traveler',
    label: 'Employee',
    desc: 'Travel & Bookings',
    icon: UserIcon
  }];
  return <div className="min-h-screen w-full flex items-center justify-center bg-navy-900 relative overflow-hidden" data-id="element-1645">
      {/* Background decoration */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808008_1px,transparent_1px),linear-gradient(to_bottom,#80808008_1px,transparent_1px)] bg-[size:24px_24px]" data-id="element-1646" />
      <div className="absolute left-0 top-0 h-48 w-48 sm:h-96 sm:w-96 rounded-full bg-navy-700/30 blur-3xl filter" data-id="element-1647" />
      <div className="absolute right-0 bottom-0 h-48 w-48 sm:h-96 sm:w-96 rounded-full bg-gold-500/10 blur-3xl filter" data-id="element-1648" />

      <div className="relative w-full max-w-4xl p-4 sm:p-6" data-id="element-1649">
        <div className="mb-6 sm:mb-8 text-center" data-id="element-1650">
          <div className="mx-auto mb-3 sm:mb-4 flex h-16 sm:h-24 w-auto items-center justify-center" data-id="element-1651">
            <img src="https://cdn.magicpatterns.com/uploads/76CAA7KBYMWEPu97mZSYz3/Untitled_design.png" alt="Super International Travels Limited" className="h-full w-auto max-w-[240px] sm:max-w-[320px] object-contain drop-shadow-2xl" data-id="element-1652" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white" data-id="element-1653">
            Super International Travels
          </h1>
          <p className="mt-1.5 sm:mt-2 text-sm sm:text-base text-slate-400" data-id="element-1654">
            Enterprise Travel Management Platform
          </p>
        </div>

        {/* Role Selector */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5 sm:gap-4 mb-6 sm:mb-8" data-id="element-1655">
          {roles.map(role => <div key={role.id} onClick={() => setSelectedRole(role.id)} className={`cursor-pointer rounded-xl p-3 sm:p-4 border transition-all ${selectedRole === role.id ? 'bg-navy-700 border-gold-500 ring-2 ring-gold-400/50' : 'bg-navy-800/50 border-navy-700 hover:bg-navy-800'}`} data-id="element-1656">
              <div className={`h-8 w-8 sm:h-10 sm:w-10 rounded-full flex items-center justify-center mb-2 sm:mb-3 ${selectedRole === role.id ? 'bg-gold-500 text-navy-900' : 'bg-navy-700 text-slate-400'}`} data-id="element-1657">
                <role.icon className="h-4 w-4 sm:h-5 sm:w-5" data-id="element-1658" />
              </div>
              <h3 className={`font-bold text-xs sm:text-base ${selectedRole === role.id ? 'text-white' : 'text-slate-200'}`} data-id="element-1659">
                {role.label}
              </h3>
              <p className={`text-[10px] sm:text-xs mt-0.5 sm:mt-1 ${selectedRole === role.id ? 'text-gold-300' : 'text-slate-500'}`} data-id="element-1660">
                {role.desc}
              </p>
            </div>)}
        </div>

        <div className="mx-auto max-w-sm sm:max-w-md rounded-2xl bg-white p-6 sm:p-8 shadow-2xl" data-id="element-1661">
          <div className="mb-5 sm:mb-6" data-id="element-1662">
            <h2 className="text-lg sm:text-xl font-semibold text-navy-900" data-id="element-1663">
              Sign in
            </h2>
            <p className="text-sm text-slate-500" data-id="element-1664">
              Access your {roles.find(r => r.id === selectedRole)?.label}{' '}
              account
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" data-id="element-1665">
            <div data-id="element-1666">
              <label className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-1667">
                Work Email
              </label>
              <div className="relative" data-id="element-1668">
                <MailIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" data-id="element-1669" />
                <input type="email" required className="block w-full rounded-lg border border-slate-300 bg-slate-50 p-2.5 pl-10 text-slate-900 placeholder-slate-400 focus:border-navy-600 focus:ring-navy-600" placeholder="name@company.com" data-id="element-1670" />
              </div>
            </div>

            <div data-id="element-1671">
              <label className="mb-1.5 block text-sm font-medium text-slate-700" data-id="element-1672">
                Password
              </label>
              <div className="relative" data-id="element-1673">
                <LockIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-400" data-id="element-1674" />
                <input type={showPassword ? 'text' : 'password'} required className="block w-full rounded-lg border border-slate-300 bg-slate-50 p-2.5 pl-10 pr-10 text-slate-900 placeholder-slate-400 focus:border-navy-600 focus:ring-navy-600" placeholder="••••••••" data-id="element-1675" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-id="element-1676">
                  {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-1677" /> : <EyeIcon className="h-5 w-5" data-id="element-1678" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between" data-id="element-1679">
              <div className="flex items-center" data-id="element-1680">
                <input id="remember-me" type="checkbox" className="h-4 w-4 rounded border-slate-300 text-navy-700 focus:ring-navy-600" data-id="element-1681" />
                <label htmlFor="remember-me" className="ml-2 text-sm text-slate-600" data-id="element-1682">
                  Remember me
                </label>
              </div>
              <a href="#" className="text-sm font-medium text-navy-700 hover:text-navy-900 hover:underline" data-id="element-1683">
                Forgot password?
              </a>
            </div>

            <button type="submit" disabled={isLoading} className="flex w-full items-center justify-center rounded-lg bg-navy-900 px-5 py-2.5 text-center text-sm font-medium text-white hover:bg-navy-700 focus:outline-none focus:ring-4 focus:ring-navy-300 disabled:opacity-70 transition-colors" data-id="element-1684">
              {isLoading ? <svg className="mr-2 h-5 w-5 animate-spin text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-1685">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-1686" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-1687" />
                </svg> : <>
                  Sign In <ArrowRightIcon className="ml-2 h-4 w-4" data-id="element-1688" />
                </>}
            </button>
          </form>

          <div className="mt-5 sm:mt-6 space-y-3" data-id="element-1689">
            <div className="relative" data-id="element-1690">
              <div className="absolute inset-0 flex items-center" data-id="element-1691">
                <div className="w-full border-t border-slate-200" data-id="element-1692" />
              </div>
              <div className="relative flex justify-center text-sm" data-id="element-1693">
                <span className="bg-white px-2 text-slate-500" data-id="element-1694">or</span>
              </div>
            </div>

            <Link to="/register" className="flex w-full items-center justify-center rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-center text-sm font-medium text-slate-700 hover:bg-slate-50" data-id="element-1695">
              New company? Register your organization
            </Link>

            <Link to="/invite" className="flex w-full items-center justify-center rounded-lg border border-transparent px-5 py-2.5 text-center text-sm font-medium text-navy-700 hover:bg-navy-50 hover:underline" data-id="element-1696">
              Have an invitation? Accept invite
            </Link>
          </div>
        </div>

        <p className="text-center text-xs text-slate-500 mt-5 sm:mt-6" data-id="element-1697">
          © 2025 Super International Travels Limited. All rights reserved.
        </p>
      </div>
    </div>;
}